<script setup>
import { requireApplicationDocuments } from "~/constants";

const { applicationId } = defineProps({
  applicationId: String,
});

const emit = defineEmits(["onChange", "closeModal"]);

const dialogVisible = ref(false);
const dialogDeclineOfferVisible = ref(false);
const dialogName = ref();

const { data: application } = await useApiFetch(
  `student-applications/${applicationId}`,
  {
    key: "student-application-detail",
    transform(application) {
      return application?.data;
    },
  },
);
const toast = useToast();

const updatedStatus = ref("booked");
const isLoading = ref(false);

const handleChangeConsulted = async () => {
  isLoading.value = true;
  await useApiFetch(`student-applications/${application.value.id}/consult`, {
    method: "PATCH",
    onResponse() {
      onUpdateResponse("consulted");
    },
  });
};

const handleChangeDocumentSubmitted = async () => {
  isLoading.value = true;
  await useApiFetch(
    `student-applications/${application.value.id}/update-status`,
    {
      method: "PATCH",
      onResponse() {
        onUpdateResponse("document_submitted");
        refreshNuxtData("my-applications");
      },
    },
  );
};

const handleAcceptOffer = async () => {
  isLoading.value = true;
  await useApiFetch(
    `student-applications/${application.value.id}/university/accept-offer`,
    {
      method: "POST",
      onResponse() {
        onUpdateResponse("student_offer_accepted");
        refreshNuxtData("my-applications");
      },
    },
  );
};

const handleDeclineOffer = async () => {
  dialogDeclineOfferVisible.value = true;
};

const onUpdateResponse = (status) => {
  refreshNuxtData("student-application-detail");
  isLoading.value = false;

  let message = "Application updated successfully.";

  switch (status) {
    case "consulted":
      message = "Consultation completed successfully.";
      break;
    case "document_submitted":
      message = "Documents submitted successfully.";
      break;
    case "student_offer_accepted":
      message = "Offer accepted successfully.";
      break;
    default:
      message = "Application status updated.";
  }

  toast.add({
    severity: "success",
    summary: message,
    life: 3000,
  });
};

const isValidSubmitDocument = computed(() => {
  return requireApplicationDocuments.every((documentName) =>
    application.value.studentApplicationDocuments.some(
      (applicationDocument) => applicationDocument.label === documentName,
    ),
  );
});

onMounted(() => {
  updatedStatus.value = application.value?.status;
});

const uploadedReceiptFile = inject("uploadedReceiptFile");

const onUploadReceipt = async () => {
  if (!uploadedReceiptFile.value) {
    toast.add({
      severity: "error",
      summary: "Please upload the receipt",
      life: 3000,
    });
    return;
  }

  isLoading.value = true;

  try {
    const formData = new FormData();
    formData.append("receipt", uploadedReceiptFile.value);

    await useApiFetch(
      `student-applications/${applicationId}/student/payment-upload`,
      {
        method: "POST",
        body: formData,
        onResponse() {
          toast.add({
            severity: "success",
            summary: "Successfully uploaded payment receipt.",
            life: 3000,
          });
          refreshNuxtData("student-application-detail");
        },
      },
    );
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Failed to upload payment receipt.",
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="text-right" v-if="application?.program">
    <!-- booked => consulted -->
    <Button
      v-if="
        application?.status === 'booked' &&
        application?.student_agent_status === 'book_confirm_by_agent'
      "
      class="px-6 py-3 text-base min-w-36"
      label="Consultation Finished"
      :loading="isLoading"
      @click="handleChangeConsulted"
    />
    <Button
      v-else-if="application?.status === 'consulted'"
      type="button"
      class="min-w-36"
      :loading="isLoading"
      label="Submit Documents"
      :disabled="!isValidSubmitDocument"
      @click="handleChangeDocumentSubmitted"
    />
    <!-- university_approved => contact | accept offer -->
    <div
      v-else-if="application?.status === 'university_approved'"
      class="flex flex-col-reverse lg:flex-row gap-y-5 lg:gap-y-0 gap-x-10 items-center justify-end"
    >
      <button
        class="text-gray-600 hover:text-red-600 text-sm"
        type="button"
        :loading="isLoading"
        @click="handleDeclineOffer"
      >
        Decline the offer
      </button>
      <Button
        type="button"
        :loading="isLoading"
        @click="handleAcceptOffer"
        class="w-fit flex flex-col items-center px-16"
      >
        <span class="font-bold">Accept the offer</span>
        <small class="text-sm text-blue-300 mt-1"
          >You need to respond to the offer within 2 weeks</small
        >
      </Button>
    </div>
    <!-- university_rejected => explore program -->
    <div
      v-else-if="application?.status === 'university_rejected'"
      class="space-x-3"
    >
      <Button
        type="button"
        label="Explore Other Programs"
        @click="navigateTo('/explore')"
        class="w-52"
      >
      </Button>
    </div>
    <!-- student_offer_accepted => upload payment receipt -->
    <div
      v-else-if="application?.status === 'student_offer_accepted'"
      class="flex flex-col-reverse lg:flex-row gap-y-5 lg:gap-y-0 gap-x-10 items-center justify-end"
    >
      <Button
        type="button"
        outlined
        label="Close"
        @click="emit('closeModal')"
        class="w-full lg:w-52 !py-4"
      />
      <Button
        type="button"
        :loading="isLoading"
        class="!py-4 w-full lg:w-52"
        label="Submit Receipt"
        @click="onUploadReceipt"
        :disabled="uploadedReceiptFile === null"
      />
    </div>
    <div v-else-if="application?.status === 'dropout'" class="space-x-3">
      <Button
        type="button"
        outlined
        label="Close"
        @click="emit('closeModal')"
        class="w-52 !py-4"
      >
      </Button>
      <Button
        type="button"
        label="Explore Other Programs"
        @click="navigateTo('/explore')"
        class="w-52 !py-4"
      >
      </Button>
    </div>
  </div>

  <Dialog
    v-model:visible="dialogVisible"
    modal
    :showHeader="false"
    class="w-[30vw]"
    :pt="{
      content: 'p-0',
    }"
  >
    <ReviseDocument
      v-if="dialogName === 'revise-document'"
      :applicationId="applicationId"
      @onClose="dialogVisible = false"
    />
    <UniversityApproved
      v-else-if="dialogName === 'university-approved'"
      :applicationId="applicationId"
      @onClose="dialogVisible = false"
    />
    <PaymentReceipt
      v-else-if="dialogName === 'upload-receipt'"
      :applicationId="applicationId"
      @onClose="dialogVisible = false"
    />
  </Dialog>

  <Dialog
    v-model:visible="dialogDeclineOfferVisible"
    modal
    header="Decline the offer"
    :style="{ width: '36rem' }"
    :pt="{
      content: 'p-0',
    }"
  >
    <DeclineOffer
      :applicationId="applicationId"
      @close="dialogDeclineOfferVisible = false"
    />
  </Dialog>
</template>
